import {
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BackupStatus,
  ResourceType,
  type InventoryResource,
} from '@repo/api-gw-sdk';
import React, { useEffect, useState } from 'react';

import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useRoles } from '@/contexts/useRoles';
import { useUser } from '@/contexts/useUser';
import { useWorkspace } from '@/contexts/useWorkspace';
import { PreferencesKey } from '@/data/dal/preferencesKey';
import type { ResourceAction } from '@/data/inventory/actions';

import { BackupPoliciesTab } from './instance/tabs/backup-policies/backupPoliciesTab';
import { EonSnapshotsTab } from './instance/tabs/eon-snapshots/eonSnapshotsTab';
import { OverviewTab } from './instance/tabs/overview-tab/overviewTab';
import { GatewayConnectionTab } from './instance/tabs/storage-gateway/gatewayConnectionTab';
import { PanelWrapper } from './panelWrapper';

import { Loader } from '../layout/loading';

export interface InstancePanelProps {
  entity: InventoryResource;
  initialTab?: ResourcePanelTabs;
  onEntityChange: () => Promise<void>;
  actions: ResourceAction[];
}

export type ResourcePanelTabs =
  | 'overview'
  | 'backup-policies'
  | 'eon-snapshots'
  | 'gateway-connection';

const tabs: {
  title: string;
  type: ResourcePanelTabs;
  predicate: (entity: InventoryResource) => boolean;
  Component: React.FC<InstancePanelProps>;
  dataTestId?: string;
}[] = [
  {
    type: 'overview',
    title: 'Overview',
    Component: OverviewTab,
    predicate: () => true,
  },
  {
    type: 'backup-policies',
    title: 'Backup policies',
    Component: BackupPoliciesTab,
    predicate: () => true,
  },
  {
    type: 'gateway-connection',
    title: 'Gateway connection',
    Component: GatewayConnectionTab,
    predicate: (entity: InventoryResource) =>
      entity.backupStatus === BackupStatus.Dlsg,
  },
  {
    type: 'eon-snapshots',
    title: 'Eon snapshots',
    Component: EonSnapshotsTab,
    dataTestId: 'eon-snapshots-tab',
    predicate: () => true,
  },
];

const InstancePanelComponent = (props: InstancePanelProps) => {
  const { initialTab } = props;

  const shownTabs = tabs.filter((x) => x.predicate(props.entity));
  const [selectedTabType, setSelectedTabType] = useState(
    initialTab || shownTabs[0].type
  );

  const selectedTab =
    shownTabs.find((x) => x.type === selectedTabType) || shownTabs[0];

  return (
    <Stack direction='column' overflow='hidden' flexGrow='1'>
      {shownTabs.length > 1 && (
        <Tabs
          onChange={(e, i) => setSelectedTabType(shownTabs[i].type)}
          value={shownTabs.indexOf(selectedTab)}
          className='px-[40px] min-h-[50px]'
          sx={{ '.MuiTabs-scroller': { display: 'flex' } }}
        >
          {shownTabs.map((value) => (
            <Tab
              data-testid={value.dataTestId}
              key={value.title}
              label={<Typography variant='subtitle1'>{value.title}</Typography>}
              className='text-[14px] font-normal'
              disableRipple
            />
          ))}
        </Tabs>
      )}
      {selectedTab?.Component && <selectedTab.Component {...props} />}
    </Stack>
  );
};

export const InstancePanel = ({
  isLoading,
  onClose,
  onBack,
  entity,
  onEntityChange,
  actions,
  initialTab,
}: {
  initialTab?: ResourcePanelTabs;
  isLoading: boolean;
  onClose: () => void;
  onBack?: () => void;
  entity: InventoryResource | undefined;
  onEntityChange: () => Promise<void>;
  actions: InstancePanelProps['actions'];
}) => {
  const { snackbar } = useWorkspace();

  useEffect(() => {
    if (!isLoading && !entity) {
      onClose();
      snackbar.showWarningMessage(
        'Looks like your link is broken and this resource ID is invalid.'
      );
    }
  }, [entity, isLoading, onClose, snackbar]);

  if (isLoading || !entity) {
    return <Loader />;
  }

  const Title = (
    <Stack direction='row' alignItems='center' justifyContent='space-between'>
      <Typography variant='h3'>Resource details</Typography>
      <Stack direction='row'>
        <ScanNowButton entity={entity} />
        <ForceFullBackupButton entity={entity} />
      </Stack>
    </Stack>
  );

  return (
    <PanelWrapper
      header={{
        title: Title,
        onClose,
        onBack,
        hideBorder: true,
      }}
      testIdPrefix='instance-panel'
    >
      <InstancePanelComponent
        entity={entity}
        onEntityChange={onEntityChange}
        actions={actions}
        initialTab={initialTab}
      />
    </PanelWrapper>
  );
};

const ScanNowButton = ({ entity }: { entity: InventoryResource }) => {
  const dal = useDAL();
  const { isAuthorizedResource } = useRoles();

  const { isDev, isDemo } = useEnvironment();
  const { isEmployee } = useUser();
  const { snackbar } = useWorkspace();
  const [disabled, setDisabled] = useState(false);

  const { data: prefResponse } = dal.preferences.useUserPref(
    PreferencesKey.Preferences
  );

  const showButton =
    isDev || (isEmployee && !isDemo && prefResponse?.value?.isDevMode);

  return (
    showButton && (
      <Tooltip title='Scan Now'>
        <IconButton
          className='ml-auto mr-20px'
          size='small'
          disabled={!isAuthorizedResource('create:jobs', entity) || disabled}
          onClick={() => {
            snackbar.showMessage('Scan Initiated');
            setDisabled(true);
            void dal.inventory.jobs
              .createScanJob(entity.id)
              .catch((e) => {
                snackbar.showMessage('Scan Failed');
                throw e;
              })
              .finally(() => {
                setDisabled(false);
              });
          }}
        >
          <i className='material-symbols-avg-pace-rounded' />
        </IconButton>
      </Tooltip>
    )
  );
};

const FULL_BACKUP_SUPPORTED_ENTITIES = [ResourceType.AwsDynamoDb];

const ForceFullBackupButton = ({ entity }: { entity: InventoryResource }) => {
  const dal = useDAL();
  const { isAuthorizedResource } = useRoles();

  const { isDev, isDemo } = useEnvironment();
  const { isEmployee } = useUser();
  const { snackbar } = useWorkspace();
  const [disabled, setDisabled] = useState(false);
  const { data: prefResponse } = dal.preferences.useUserPref(
    PreferencesKey.Preferences
  );

  const showButton =
    isDev || (isEmployee && !isDemo && prefResponse?.value?.isDevMode);

  if (
    !FULL_BACKUP_SUPPORTED_ENTITIES.includes(entity.resourceType) ||
    !showButton
  ) {
    return null;
  }

  return (
    <Tooltip title='Force Full Backup'>
      <IconButton
        className='ml-auto mr-20px'
        size='small'
        disabled={!isAuthorizedResource('create:jobs', entity) || disabled}
        onClick={() => {
          snackbar.showMessage('Marked for full backup');
          setDisabled(true);
          void dal.inventory.jobs
            .forceFullBackupForResource(entity.id)
            .catch((e: unknown) => {
              snackbar.showMessage('Failed to force full backup');
              throw e;
            })
            .finally(() => {
              setDisabled(false);
            });
        }}
      >
        <i className='material-symbols-schedule-outline' />
      </IconButton>
    </Tooltip>
  );
};
